import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faImage, 
  faSearch, 
  faRefresh, 
  faSpinner,
  faExclamationTriangle 
} from '@fortawesome/free-solid-svg-icons';
import { useNotification } from '../../context/NotificationContext';
import { getApiUrl } from '../../config/api';
import { LibraryItem } from '../../types';
import styles from './LibraryImageModal.module.css';

interface LibraryImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageSelected: (item: LibraryItem) => void;
  projectId: string;
  title?: string;
  subtitle?: string;
  selectButtonText?: string;
  allowMultiSelect?: boolean;
  maxSelections?: number;
  filterFunction?: (item: LibraryItem) => boolean;
  sortFunction?: (a: LibraryItem, b: LibraryItem) => number;
  customActions?: {
    label: string;
    icon?: any;
    action: (item: LibraryItem) => void;
    variant?: 'primary' | 'secondary' | 'danger';
  }[];
}

const LibraryImageModal: React.FC<LibraryImageModalProps> = ({
  isOpen,
  onClose,
  onImageSelected,
  projectId,
  title = 'Select Image from Library',
  subtitle = 'Choose an image to use',
  selectButtonText = 'Select Image',
  allowMultiSelect = false,
  maxSelections = 1,
  filterFunction,
  sortFunction,
  customActions = []
}) => {
  const { t } = useTranslation('thumbnailModal');
  const [libraryItems, setLibraryItems] = useState<LibraryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<LibraryItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<LibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const { showNotification } = useNotification();
  // Load library items when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      loadLibraryItems();
    }
  }, [isOpen, projectId]);

  // Apply filters when items or search term changes
  useEffect(() => {
    applyFilters();
  }, [libraryItems, searchTerm, filterFunction]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedItems([]);
      setSearchTerm('');
      setError(null);
    }
  }, [isOpen]);

  const loadLibraryItems = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(getApiUrl(`/media/library/${projectId}`), {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const data = await response.json();
      
      if (data.success) {
        // Filter to images only by default
        let items = data.data.filter((item: LibraryItem) => item.type === 'image');
        
        // Apply custom filter if provided
        if (filterFunction) {
          items = items.filter(filterFunction);
        }
        
        // Apply custom sort or default to newest first
        if (sortFunction) {
          items.sort(sortFunction);
        } else {
          items.sort((a: LibraryItem, b: LibraryItem) => 
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        }
        
        setLibraryItems(items);
      } else {
        throw new Error(data.message || 'Failed to fetch library');
      }
    } catch (error) {
      console.error('Error loading library items:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(t('thumbnailModal.libraryModal.image.error', { error: errorMessage }));
      showNotification({
        title: t('common.error'),
        message: t('thumbnailModal.libraryModal.image.error', { error: errorMessage }),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const applyFilters = () => {
    let filtered = libraryItems;
    
    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(term) ||
        (item.prompt && item.prompt.toLowerCase().includes(term))
      );
    }
    
    setFilteredItems(filtered);
  };

  const handleItemClick = (item: LibraryItem) => {
    if (allowMultiSelect) {
      setSelectedItems(prev => {
        const isSelected = prev.some(selected => selected.id === item.id);
        if (isSelected) {
          return prev.filter(selected => selected.id !== item.id);
        } else if (prev.length < maxSelections) {
          return [...prev, item];
        } else {
          showNotification({
            title: t('thumbnailModal.libraryModal.image.selectionLimit'),
            message: t('thumbnailModal.libraryModal.image.selectionLimitMessage', { max: maxSelections }),
            type: 'warning'
          });
          return prev;
        }
      });
    } else {
      setSelectedItems([item]);
    }
  };

  const handleSelect = () => {
    if (selectedItems.length === 0) {
      showNotification({
        title: t('thumbnailModal.libraryModal.image.noSelection'),
        message: t('thumbnailModal.libraryModal.image.noSelectionMessage'),
        type: 'warning'
      });
      return;
    }

    if (allowMultiSelect) {
      // For multi-select, call callback for each item
      selectedItems.forEach(item => onImageSelected(item));
    } else {
      // For single select, call callback with the selected item
      onImageSelected(selectedItems[0]);
    }
    
    onClose();
  };

  const handleCustomAction = (action: any, item: LibraryItem) => {
    action.action(item);
  };

  const isItemSelected = (item: LibraryItem) => {
    return selectedItems.some(selected => selected.id === item.id);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h2 className={styles.title}>
              <FontAwesomeIcon icon={faImage} className={styles.titleIcon} />
              {title}
            </h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        {/* Controls */}
        <div className={styles.controls}>
          <div className={styles.searchSection}>
            <div className={styles.searchInput}>
              <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
              <input
                type="text"
                placeholder={t('thumbnailModal.libraryModal.image.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button 
              onClick={loadLibraryItems} 
              className={styles.refreshButton}
              disabled={isLoading}
              title={t('thumbnailModal.libraryModal.image.refreshTitle')}
            >
              <FontAwesomeIcon 
                icon={isLoading ? faSpinner : faRefresh} 
                className={isLoading ? styles.spinning : ''}
              />
            </button>
          </div>
          
          {allowMultiSelect && (
            <div className={styles.selectionInfo}>
              {t('thumbnailModal.libraryModal.image.selected', { count: selectedItems.length, max: maxSelections })}
            </div>
          )}
        </div>

        {/* Content */}
        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <FontAwesomeIcon icon={faSpinner} className={styles.spinner} />
              <p>{t('thumbnailModal.libraryModal.image.loading')}</p>
            </div>
          ) : error ? (
            <div className={styles.errorState}>
              <FontAwesomeIcon icon={faExclamationTriangle} className={styles.errorIcon} />
              <p className={styles.errorMessage}>{error}</p>
              <button onClick={loadLibraryItems} className={styles.retryButton}>
                {t('thumbnailModal.libraryModal.image.tryAgain')}
              </button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faImage} className={styles.emptyIcon} />
              <p>{t('thumbnailModal.libraryModal.image.noImages')}</p>
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')} 
                  className={styles.clearSearchButton}
                >
                  {t('thumbnailModal.libraryModal.image.clearSearch')}
                </button>
              )}
            </div>
          ) : (
            <div className={styles.imageGrid}>
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className={`${styles.imageItem} ${isItemSelected(item) ? styles.selected : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                  <div className={styles.imageContainer}>
                    <img src={item.url} alt={item.name} className={styles.image} />
                    {isItemSelected(item) && (
                      <div className={styles.selectedOverlay}>
                        <FontAwesomeIcon icon={faImage} />
                      </div>
                    )}
                  </div>
                  <div className={styles.imageInfo}>
                    <p className={styles.imageName} title={item.name}>
                      {item.name}
                    </p>
                    <div className={styles.imageActions}>
                      {customActions.map((action, index) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCustomAction(action, item);
                          }}
                          className={`${styles.actionButton} ${styles[action.variant || 'secondary']}`}
                          title={action.label}
                        >
                          {action.icon && <FontAwesomeIcon icon={action.icon} />}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            {filteredItems.length > 0 && (
              <span className={styles.itemCount}>
                {t(filteredItems.length === 1 ? 'libraryModal.image.available' : 'libraryModal.image.available_plural', { count: filteredItems.length })}
              </span>
            )}
          </div>
          <div className={styles.footerActions}>
            <button onClick={onClose} className={styles.cancelButton}>
              {t('thumbnailModal.libraryModal.image.cancel')}
            </button>
            <button 
              onClick={handleSelect} 
              className={styles.selectButton}
              disabled={selectedItems.length === 0}
            >
              {selectButtonText}
              {allowMultiSelect && selectedItems.length > 0 && ` (${selectedItems.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibraryImageModal;